// Framework-agnostic keyboard engine: one document keydown listener that matches
// single keys, modifier combos (mod+k) and sequential chords (g e, d l b), while
// respecting active scopes and ignoring keystrokes typed into form fields.
//
// Authored IDENTICALLY across apps/web, apps/perps and marginx — keep in sync.

import type { ChordToken, ShortcutDef, ShortcutHandler } from "./types";

// A pending chord (and its which-key hint) stays live this long before it gives
// up — long enough to glance at the options bar and press the next key.
const CHORD_TIMEOUT_MS = 2500;

/** Normalize a KeyboardEvent into a binding token, or null for pure modifiers. */
export function tokenize(e: KeyboardEvent): ChordToken | null {
  const k = e.key;
  // Some events (IME composition, autofill, and keydowns dispatched by portalled
  // SDK modals like Privy) arrive with no `key` — bail instead of crashing on
  // `k.toLowerCase()`.
  if (!k || k === "Shift" || k === "Control" || k === "Alt" || k === "Meta") {
    return null;
  }

  let base: string;
  switch (k) {
    case " ":
      base = "space";
      break;
    case "Escape":
      base = "esc";
      break;
    case "Enter":
      base = "enter";
      break;
    case "ArrowUp":
      base = "up";
      break;
    case "ArrowDown":
      base = "down";
      break;
    case "ArrowLeft":
      base = "left";
      break;
    case "ArrowRight":
      base = "right";
      break;
    case "Backspace":
      base = "backspace";
      break;
    case "Delete":
      base = "del";
      break;
    case "Tab":
      base = "tab";
      break;
    default:
      base = k.toLowerCase();
  }

  const mods: string[] = [];
  if (e.metaKey || e.ctrlKey) mods.push("mod");
  if (e.altKey) mods.push("alt");
  // Only fold Shift into the token for non-printable bases (e.g. shift+space,
  // shift+tab). Printable shifted chars already encode shift in `key` (e.g. "?").
  if (e.shiftKey && (base.length > 1 || base === "space")) mods.push("shift");

  return mods.length ? [...mods, base].join("+") : base;
}

/**
 * Roles whose widget owns the arrow keys for its own navigation.
 *
 * ARIA composite widgets move focus among their options with the arrows: a
 * radiogroup, a listbox, a tablist, a slider, a menu. A global single-arrow
 * shortcut must not reach across and act on the page while the user is
 * driving one of them.
 *
 * This is not hypothetical. `trade.cycleLeft` / `cycleRight` are bound to the
 * bare `left` / `right` keys, and this manager listens in the CAPTURE phase
 * and calls both preventDefault() and stopPropagation() when it matches. So
 * pressing Right with a max-slippage preset focused switched the order type
 * from Market to Limit and unmounted the control, and the component's own
 * onKeyDown never ran at all -- it could not have, whatever it tried.
 */
const ARROW_OWNING_ROLES = new Set([
  "radio",
  "radiogroup",
  "listbox",
  "option",
  "combobox",
  "slider",
  "spinbutton",
  "tab",
  "tablist",
  "menu",
  "menubar",
  "menuitem",
  "menuitemradio",
  "menuitemcheckbox",
  "tree",
  "treeitem",
  "grid",
  "gridcell",
]);

const ARROW_TOKENS = new Set(["up", "down", "left", "right", "home", "end"]);

/**
 * True when this keystroke is an arrow (or Home/End) and focus sits inside a
 * widget that navigates with them. Checked on the element and its ancestors,
 * because the focused item is a child of the container that carries the role.
 */
/**
 * Arrow-owning widgets that carry no `role` attribute because the element
 * already means it.
 *
 * `<input type="range">` is the one that bit us: its role is `slider`
 * implicitly, `ARROW_OWNING_ROLES` already contains "slider", and
 * `isEditableTarget` deliberately excludes range (it is not text entry) -- so a
 * native slider fell through BOTH guards. Arrowing the deposit modal's amount
 * slider left it where it was and switched the order type on the page behind
 * it, because nothing deactivates the `trade` scope while a modal is open.
 */
const ownsArrowKeysImplicitly = (el: HTMLElement): boolean => {
  const tag = el.tagName ? el.tagName.toLowerCase() : "";
  if (tag === "select") return true;
  if (tag === "input" && (el as HTMLInputElement).type === "range") return true;
  // A control that opens a listbox navigates it with the arrows even when the
  // role sits on the popup rather than on the control.
  if (el.getAttribute("aria-haspopup") === "listbox") return true;
  return false;
};

export function ownsArrowKeys(token: string, target: EventTarget | null): boolean {
  if (!ARROW_TOKENS.has(token)) return false;
  let el = target as HTMLElement | null;
  // A bounded walk: deep enough for option-inside-listbox, cheap on every key.
  for (let depth = 0; el && depth < 6; depth += 1) {
    const role = el.getAttribute("role");
    if (role && ARROW_OWNING_ROLES.has(role)) return true;
    if (ownsArrowKeysImplicitly(el)) return true;
    el = el.parentElement;
  }
  return false;
}

/** True if focus is in a place where the user is typing (so we should stand down). */
export function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") {
    // Exception: range/checkbox/radio/button inputs aren't text entry.
    if (tag === "input") {
      const type = (el as HTMLInputElement).type;
      if (type === "checkbox" || type === "radio" || type === "button" || type === "range") {
        return false;
      }
    }
    return true;
  }
  if (el.isContentEditable) return true;
  return false;
}

/**
 * True if focus is in a field where the user types free TEXT (letters are
 * content) — an email/search/text input, a textarea, or contentEditable.
 * Numeric fields (type=number, or inputmode numeric/decimal — e.g. the trade
 * form's size/price) are NOT text entry: letters aren't valid there, so
 * letter-based shortcuts may still fire. This is what lets us keep the trade
 * D-chords working while a size/price field is focused, yet stand down inside a
 * real text field like the wallet-login email.
 */
export function isTextEntryTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === "textarea") return true;
  if (tag === "input") {
    const input = el as HTMLInputElement;
    const mode = (input.getAttribute("inputmode") || "").toLowerCase();
    if (mode === "numeric" || mode === "decimal") return false;
    const type = (input.getAttribute("type") || "text").toLowerCase();
    return ["text", "email", "password", "search", "tel", "url", ""].includes(type);
  }
  if (el.isContentEditable) return true;
  return false;
}

/**
 * True if a shortcut's first key is a bare printable character (a single char
 * with no modifier, e.g. "d" or "?"). Such a key is indistinguishable from typed
 * text, so it must never start a shortcut/chord while a text field is focused —
 * otherwise the keystroke gets swallowed and the user can't type that letter.
 */
function startsWithBarePrintableKey(s: ShortcutDef): boolean {
  const first = s.keys[0];
  return typeof first === "string" && first.length === 1 && !first.includes("+");
}

function arraysEqual(a: ChordToken[], b: ChordToken[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

function isPrefix(prefix: ChordToken[], full: ChordToken[]): boolean {
  return prefix.length < full.length && prefix.every((v, i) => v === full[i]);
}

export class KeyboardManager {
  private registry: ShortcutDef[] = [];
  // A STACK of handlers per shortcut id. Several components can bind the same id
  // at once (e.g. multiple tab strips binding "tabs.next"); on a key we walk the
  // stack newest-first and fire the first handler that doesn't pass through
  // (returns `false`). This lets a gated handler decline (e.g. "not the hovered
  // panel") so a sibling — or none — handles the key instead.
  private handlers = new Map<string, ShortcutHandler[]>();
  private scopes: string[] = ["global"]; // stack; "global" always present
  private buffer: ChordToken[] = [];
  private chordTimer: ReturnType<typeof setTimeout> | null = null;
  private bufferListeners = new Set<(buffer: ChordToken[]) => void>();
  private started = false;

  constructor(registry: ShortcutDef[]) {
    this.registry = registry;
    this.onKeyDown = this.onKeyDown.bind(this);
  }

  start(): void {
    if (this.started || typeof document === "undefined") return;
    document.addEventListener("keydown", this.onKeyDown, true);
    this.started = true;
  }

  stop(): void {
    if (!this.started || typeof document === "undefined") return;
    document.removeEventListener("keydown", this.onKeyDown, true);
    this.clearBuffer();
    this.started = false;
  }

  /** Push a handler onto an id's stack (most-recent fires first). */
  addHandler(id: string, handler: ShortcutHandler): void {
    const stack = this.handlers.get(id);
    if (stack) stack.push(handler);
    else this.handlers.set(id, [handler]);
  }

  /** Remove a previously-added handler (by identity); cleans up empty stacks. */
  removeHandler(id: string, handler: ShortcutHandler): void {
    const stack = this.handlers.get(id);
    if (!stack) return;
    const i = stack.lastIndexOf(handler);
    if (i >= 0) stack.splice(i, 1);
    if (!stack.length) this.handlers.delete(id);
  }

  /** True if at least one handler is currently bound to this id. */
  hasHandler(id: string): boolean {
    return (this.handlers.get(id)?.length ?? 0) > 0;
  }

  /**
   * Fire the handlers for `id` newest-first, stopping at the first that consumes
   * the event (returns anything but `false`). Returns true if one consumed it.
   */
  private dispatch(id: string, e: KeyboardEvent): boolean {
    const stack = this.handlers.get(id);
    if (!stack) return false;
    // Snapshot: a handler may unmount others mid-dispatch.
    for (const handler of [...stack].reverse()) {
      if (handler(e) !== false) return true;
    }
    return false;
  }

  pushScope(scope: string): void {
    this.scopes.push(scope);
    this.emitBuffer(); // notify the hint bar so it reflects the new active scope
  }

  popScope(scope: string): void {
    const i = this.scopes.lastIndexOf(scope);
    if (i > 0) this.scopes.splice(i, 1); // never pop "global"
    this.emitBuffer();
  }

  isScopeActive(scope: string): boolean {
    return scope === "global" || this.scopes.includes(scope);
  }

  /** Current pending chord buffer (for the hint bar to echo "G …"). */
  getBuffer(): ChordToken[] {
    return this.buffer;
  }

  onBufferChange(cb: (buffer: ChordToken[]) => void): () => void {
    this.bufferListeners.add(cb);
    return () => this.bufferListeners.delete(cb);
  }

  private emitBuffer(): void {
    for (const cb of this.bufferListeners) cb(this.buffer);
  }

  private clearBuffer(): void {
    if (this.chordTimer) {
      clearTimeout(this.chordTimer);
      this.chordTimer = null;
    }
    if (this.buffer.length) {
      this.buffer = [];
      this.emitBuffer();
    }
  }

  /** Shortcuts available right now (active scope + input rules). */
  private candidates(inInput: boolean, isTextEntry = false): ShortcutDef[] {
    return this.registry.filter(
      (s) =>
        this.isScopeActive(s.scope) &&
        this.hasHandler(s.id) &&
        (!inInput || s.allowInInput) &&
        // Inside a real text field, a bare printable key must reach the field as
        // typed text — never hijack it as a shortcut/chord. (Fixes e.g. being
        // unable to type an email that contains "d" while the D-chord is armed.)
        (!isTextEntry || !startsWithBarePrintableKey(s)),
    );
  }

  private onKeyDown(e: KeyboardEvent): void {
    const token = tokenize(e);
    if (token === null) return; // pure modifier

    // Esc always cancels a pending chord (and lets other esc handlers run too).
    if (token === "esc" && this.buffer.length) {
      this.clearBuffer();
      // fall through so an "esc" shortcut (e.g. close modal) can still match
    }

    // Focus is driving a radiogroup, listbox, slider or similar: the arrows
    // belong to it, not to the page. Stand down before matching, so the
    // widget's own handler sees the key.
    if (ownsArrowKeys(token, e.target)) {
      this.clearBuffer();
      return;
    }

    const inInput = isEditableTarget(e.target);
    const isTextEntry = isTextEntryTarget(e.target);
    const candidates = this.candidates(inInput, isTextEntry);
    const next = [...this.buffer, token];

    const exact = candidates.find((s) => arraysEqual(s.keys, next));
    const hasPrefix = candidates.some((s) => isPrefix(next, s.keys));

    if (exact && !hasPrefix) {
      // Unambiguous match → fire the handler stack until one consumes it.
      this.clearBuffer();
      if (this.dispatch(exact.id, e)) {
        e.preventDefault();
        e.stopPropagation();
      }
      return;
    }

    if (hasPrefix) {
      // Chord in progress (and maybe also an exact match for a shorter binding —
      // we wait to disambiguate the longer one). Hold the buffer.
      this.buffer = next;
      this.emitBuffer();
      e.preventDefault();
      e.stopPropagation();
      if (this.chordTimer) clearTimeout(this.chordTimer);
      this.chordTimer = setTimeout(() => {
        // Timeout: if the buffer is itself an exact binding, fire it; else drop.
        const fallback = this.candidates(
          isEditableTarget(document.activeElement),
          isTextEntryTarget(document.activeElement),
        ).find((s) => arraysEqual(s.keys, this.buffer));
        const id = fallback?.id;
        this.clearBuffer();
        if (id) this.dispatch(id, e);
      }, CHORD_TIMEOUT_MS);
      return;
    }

    // No match starting from the current buffer.
    if (this.buffer.length) {
      // The buffer led nowhere; reset and re-evaluate this key from scratch so a
      // fresh chord can start (e.g. user typed "g x" then "d").
      this.clearBuffer();
      const fresh = [token];
      const freshExact = candidates.find((s) => arraysEqual(s.keys, fresh));
      const freshPrefix = candidates.some((s) => isPrefix(fresh, s.keys));
      if (freshExact && !freshPrefix) {
        this.clearBuffer();
        if (this.dispatch(freshExact.id, e)) {
          e.preventDefault();
          e.stopPropagation();
        }
      } else if (freshPrefix) {
        this.buffer = fresh;
        this.emitBuffer();
        e.preventDefault();
        if (this.chordTimer) clearTimeout(this.chordTimer);
        this.chordTimer = setTimeout(() => this.clearBuffer(), CHORD_TIMEOUT_MS);
      }
    }
  }
}
